import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { describeMergedInto } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import { useTenantCustomerAddManyMutation } from "@/v2-backoffice-client/tenant/customer/add/many/tenant.customer.add.many.mutation"

export type TenantCustomerAddManySelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect"
> & {
  customer: CustomerFragment
  tenantIds: number[]
  onSelect?(): void
}

/**
 * One candidate customer for the selected teams. A customer whose company
 * HubSpot merged away cannot be picked: the row says which customer to
 * pick instead.
 */
function TenantCustomerAddManySelectorItem(
  props: TenantCustomerAddManySelectorItemProps,
) {
  const { customer, tenantIds, onSelect, ...selectorItemProps } = props

  const [assignMany, { loading, error }] = useTenantCustomerAddManyMutation()
  errorToast(error, {
    title: "Failed to update the teams' customer",
  })

  const isLinked = Boolean(customer.ExternalCustomerId)
  const mergedAway = describeMergedInto(customer)

  function describe() {
    if (mergedAway) return mergedAway
    if (!isLinked) {
      return "Not linked to HubSpot — link a company before assigning"
    }
    return customer.ExternalCustomerId
  }

  return (
    <Selector.Item
      isLoading={loading}
      value={`${customer.Name ?? ""} ${customer.ExternalCustomerId ?? ""}`}
      disabled={!isLinked || Boolean(mergedAway)}
      onSelect={() => {
        // Tenant assignment still keys on the legacy HubSpot id; an
        // app-created customer must be linked to a company first.
        if (!customer.ExternalCustomerId) return
        assignMany({
          variables: {
            tenantIds,
            externalCustomerId: customer.ExternalCustomerId,
          },
        })
        onSelect?.()
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        primary={customer.Name ?? "Unnamed"}
        secondary={describe()}
      />
    </Selector.Item>
  )
}

export default TenantCustomerAddManySelectorItem
